'use client'

import { useCallback, useEffect, useRef, useState, useTransition } from 'react'
import { useExtracted, useLocale } from 'next-intl'
import { usePathname as useNextPathname, useRouter as useNextRouter } from 'next/navigation'
import { useRouter as useIntlRouter, usePathname as useIntlPathname } from '@/i18n/navigation'
import { type AppLocale } from '@/i18n/routing'
import { isAppRoute, stripLocalePrefix } from '@/i18n/surfaces'
import { useLocaleSettings } from '@/i18n/useLocaleSettings'
import { updateLocalePreference } from '@/lib/auth'
import { LOCALE_DISPLAY, persistLocaleChoice } from '@/lib/locale'
import { cn } from '@/lib/utils'
import { Check, ChevronRight, Globe } from '@/components/ui/icons'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const LOCALE_OPTIONS = LOCALE_DISPLAY

type Variant = 'dropdown' | 'menu'

type Props = {
  /**
   * 'dropdown' — compact select for nav bars and other standalone placements.
   * 'menu' — list of locales, suitable for user-menu placements.
   */
  variant?: Variant
  className?: string
}

export function LocaleSwitcher({ variant = 'dropdown', className }: Props) {
  const t = useExtracted()
  const current = useLocale() as AppLocale
  // Raw pathname (e.g. `/ja/pricing`) for surface classification.
  const rawPathname = useNextPathname() ?? '/'
  // Locale-stripped pathname (e.g. `/pricing`) for the URL-replace target on
  // localized surfaces. next-intl's hook reads from NextIntlClientProvider
  // which both root layouts install.
  const intlPathname = useIntlPathname()
  const intlRouter = useIntlRouter()
  const nextRouter = useNextRouter()
  const [isPending, startTransition] = useTransition()
  // Only offer publicly-enabled locales here, even to editors. Selecting a
  // launch-gated locale from this switcher cannot actually take effect: on app
  // surfaces `resolveAppLocale` ignores a hidden-locale cookie, and on marketing
  // surfaces middleware 307-strips the locale prefix. Editors preview hidden
  // locales through the `/translate/{locale}` tool, which has the same-origin
  // referer signal that authorizes the bypass.
  const { settings, visibleLocales } = useLocaleSettings()
  // Always include the active locale even if it's gated — otherwise the switcher
  // becomes inert mid-session if an admin disables the locale the user is on.
  const localesToShow: AppLocale[] = visibleLocales.includes(current) ? visibleLocales : [...visibleLocales, current]

  const onAppSurface = isAppRoute(stripLocalePrefix(rawPathname).pathname)

  const switchTo = (next: AppLocale) => {
    if (next === current) return
    // Persist explicit choice so it survives full page loads and beats Accept-Language.
    persistLocaleChoice(next)
    updateLocalePreference(next).catch(error => {
      console.warn('[LocaleSwitcher] Failed to sync locale preference:', error)
    })

    if (onAppSurface) {
      // App URLs are locale-agnostic — refresh the server tree so the (app)
      // layout re-resolves locale from the freshly-set cookie.
      startTransition(() => {
        nextRouter.refresh()
      })
      return
    }

    // Marketing/learn: URL carries the locale; replace it. Preserve the
    // current query string — pages like /invite?code=... lose flow state if
    // locale switching drops the search params. Read directly from
    // window.location instead of useSearchParams() to avoid forcing the page
    // into dynamic rendering (which breaks statically-prerendered routes).
    const search = typeof window !== 'undefined' ? window.location.search : ''
    const target = search ? `${intlPathname}${search}` : intlPathname
    startTransition(() => {
      intlRouter.replace(target, { locale: next })
    })
  }

  if (variant === 'menu') {
    return (
      <LocaleMenuPopout
        current={current}
        isPending={isPending}
        switchTo={switchTo}
        className={className}
        locales={localesToShow}
        settings={settings}
      />
    )
  }

  // If only the default locale is visible, skip rendering the switcher entirely —
  // there's nothing to switch to.
  if (localesToShow.length <= 1) return null

  return (
    <Select value={current} onValueChange={value => switchTo(value as AppLocale)} disabled={isPending}>
      <SelectTrigger
        aria-label={t('Language')}
        className={cn(
          'h-10 min-w-[8.5rem] rounded-full border-white/15 bg-white/5 px-4 py-0 text-sm font-medium text-white shadow-none hover:bg-white/10 focus-visible:ring-white/35 disabled:opacity-70 [&>svg]:text-white/60',
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        align="end"
        className="z-[200] border-white/15 bg-zinc-950/95 text-white shadow-xl backdrop-blur-xl"
      >
        {localesToShow.map(locale => (
          <SelectItem
            key={locale}
            value={locale}
            aria-label={LOCALE_OPTIONS[locale].ariaLabel}
            className="text-sm text-white/80 focus:bg-white/10 focus:text-white data-[state=checked]:text-white"
          >
            <LocaleOptionLabel locale={locale} isHidden={settings[locale]?.enabled !== true} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

type LocaleMenuPopoutProps = {
  current: AppLocale
  isPending: boolean
  switchTo: (next: AppLocale) => void
  className?: string
  locales: AppLocale[]
  settings: ReturnType<typeof useLocaleSettings>['settings']
}

const POPOUT_WIDTH = 176 // matches w-44

function LocaleMenuPopout({ current, isPending, switchTo, className, locales, settings }: LocaleMenuPopoutProps) {
  const t = useExtracted()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoutRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    // Place to the left of the trigger by default; flip to the right if there isn't room.
    const gap = 8
    const left = rect.left - POPOUT_WIDTH - gap >= 8 ? rect.left - POPOUT_WIDTH - gap : rect.right + gap
    setPosition({ top: rect.top, left })
  }, [])

  useEffect(() => {
    if (!open) return
    const frame = window.requestAnimationFrame(updatePosition)
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (popoutRef.current?.contains(target)) return
      close()
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, close, updatePosition])

  if (locales.length <= 1) return null

  return (
    <div className={className}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground"
      >
        <Globe className="h-4 w-4" />
        <span className="flex-1 text-start">{t('Language')}</span>
        <span className="text-xs text-muted-foreground/80">{LOCALE_OPTIONS[current].label}</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
      </button>

      {open && position && (
        <div
          ref={popoutRef}
          role="menu"
          aria-label={t('Language')}
          style={{ position: 'fixed', top: position.top, left: position.left, width: POPOUT_WIDTH }}
          className="z-[110] overflow-hidden rounded-lg border border-border bg-popover shadow-lg"
        >
          {locales.map(locale => {
            const isActive = locale === current
            return (
              <button
                key={locale}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => {
                  if (!isActive) switchTo(locale)
                  close()
                }}
                disabled={isPending}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2.5 text-sm transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
                )}
              >
                <LocaleOptionLabel locale={locale} isHidden={settings[locale]?.enabled !== true} />
                {isActive && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function LocaleOptionLabel({ locale, isHidden }: { locale: AppLocale; isHidden: boolean }) {
  const t = useExtracted()
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{LOCALE_OPTIONS[locale].label}</span>
      {isHidden && (
        <span className="shrink-0 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none tracking-normal text-muted-foreground">
          {t('hidden')}
        </span>
      )}
    </span>
  )
}
